import {
    ActionIcon,
    Badge,
    Button,
    Group,
    MultiSelect,
    Paper,
    Select,
    Stack,
    Text,
    Textarea,
    TextInput,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
    AttractionFactCandidateRO,
    AttractionStoryBrief,
    AttractionStoryBriefCard,
    AttractionStoryBriefStatusEnum,
} from '@routellers/types/dist/city-attraction/attraction-fact';
import { IconArrowDown, IconArrowUp, IconPlus, IconTrash } from '@tabler/icons-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { api, apiErrorMessage } from '../../api';
import { Section } from '../../components/editor';

import { isChosen } from './facts-panel';

type Draft = {
    promise: string;
    spine: string;
    hookIdea: string;
    beadIds: number[];
    cards: AttractionStoryBriefCard[];
    funFactId: number | null;
    lookForIds: number[];
    notes: string;
};

const EMPTY: Draft = {
    promise: '',
    spine: '',
    hookIdea: '',
    beadIds: [],
    cards: [],
    funFactId: null,
    lookForIds: [],
    notes: '',
};

function toDraft(brief: AttractionStoryBrief | null): Draft {
    if (!brief) return EMPTY;

    return {
        promise: brief.promise || '',
        spine: brief.spine || '',
        hookIdea: brief.hookIdea || '',
        beadIds: brief.beadIds || [],
        cards: brief.cards || [],
        funFactId: brief.funFactId ?? null,
        lookForIds: brief.lookForIds || [],
        notes: brief.notes || '',
    };
}

const ids = (values: string[]) => values.map(Number);
const strs = (values: number[]) => values.map(String);

export const StoryBriefPanel = ({
    attractionId,
    onChanged,
}: {
    attractionId: number;
    onChanged?: () => void | Promise<void>;
}) => {
    const { t } = useTranslation();
    const [facts, setFacts] = useState<AttractionFactCandidateRO[]>([]);
    const [brief, setBrief] = useState<AttractionStoryBrief | null>(null);
    const [draft, setDraft] = useState<Draft>(EMPTY);
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        const [factsRes, briefRes] = await Promise.all([
            api.cityAttractionsApi.listFacts(attractionId),
            api.cityAttractionsApi.getStoryBrief(attractionId),
        ]);
        setFacts(factsRes.data);
        setBrief(briefRes.data.brief);
        setDraft(toDraft(briefRes.data.brief));
    }, [attractionId]);

    useEffect(() => {
        load().catch((error) =>
            notifications.show({ color: 'red', message: apiErrorMessage(error) }),
        );
    }, [load]);

    const byId = useMemo(() => new Map(facts.map((f) => [f.id, f])), [facts]);
    const options = useMemo(() => {
        const referenced = new Set([
            ...draft.beadIds,
            ...draft.lookForIds,
            ...draft.cards.flatMap((c) => c.factIds),
            ...(draft.funFactId ? [draft.funFactId] : []),
        ]);

        return facts
            .filter((f) => isChosen(f) || referenced.has(f.id))
            .map((f) => ({
                value: String(f.id),
                label: `#${f.id} ${f.claim.length > 110 ? `${f.claim.slice(0, 110)}…` : f.claim}`,
            }));
    }, [facts, draft]);

    const set = <K extends keyof Draft>(key: K, value: Draft[K]) =>
        setDraft((d) => ({ ...d, [key]: value }));

    const moveBead = (index: number, delta: number) => {
        const next = [...draft.beadIds];
        const target = index + delta;
        if (target < 0 || target >= next.length) return;
        [next[index], next[target]] = [next[target], next[index]];
        set('beadIds', next);
    };

    const save = async (status: AttractionStoryBriefStatusEnum) => {
        setSaving(true);
        try {
            const { data } = await api.cityAttractionsApi.saveStoryBrief({
                id: attractionId,
                data: {
                    promise: draft.promise,
                    spine: draft.spine,
                    hookIdea: draft.hookIdea || null,
                    beadIds: draft.beadIds,
                    cards: draft.cards.map((c) => ({
                        titleIdea: c.titleIdea || null,
                        factIds: c.factIds,
                    })),
                    funFactId: draft.funFactId,
                    lookForIds: draft.lookForIds,
                    notes: draft.notes || null,
                    status,
                },
            });
            setBrief(data.brief);
            setDraft(toDraft(data.brief));
            notifications.show({ color: 'green', message: t('editors.attractionBrief.saved') });
            onChanged?.();
        } catch (error) {
            notifications.show({ color: 'red', message: apiErrorMessage(error) });
        } finally {
            setSaving(false);
        }
    };

    const approved = brief?.status === AttractionStoryBriefStatusEnum.APPROVED;

    return (
        <Stack gap='md' pb={40}>
            <Section
                boxed
                title={t('editors.attractionBrief.title')}
                description={t('editors.attractionBrief.description')}
            >
                <Group gap='xs'>
                    <Badge color={approved ? 'green' : 'gray'}>
                        {brief
                            ? t(`editors.attractionBrief.status.${brief.status}`)
                            : t('editors.attractionBrief.none')}
                    </Badge>
                    {brief && (
                        <Text size='xs' c='dimmed'>
                            {t(`editors.attractionBrief.author.${brief.author}`)}
                            {brief.approvedByEmail ? ` · ${brief.approvedByEmail}` : ''}
                            {brief.updatedAt
                                ? ` · ${new Date(brief.updatedAt).toLocaleString()}`
                                : ''}
                        </Text>
                    )}
                </Group>
                <TextInput
                    label={t('editors.attractionBrief.promise')}
                    description={t('editors.attractionBrief.promiseHint')}
                    value={draft.promise}
                    onChange={(e) => set('promise', e.currentTarget.value)}
                />
                <Textarea
                    label={t('editors.attractionBrief.spine')}
                    description={t('editors.attractionBrief.spineHint')}
                    autosize
                    minRows={2}
                    value={draft.spine}
                    onChange={(e) => set('spine', e.currentTarget.value)}
                />
                <TextInput
                    label={t('editors.attractionBrief.hookIdea')}
                    value={draft.hookIdea}
                    onChange={(e) => set('hookIdea', e.currentTarget.value)}
                />
            </Section>

            <Section
                boxed
                title={t('editors.attractionBrief.beads')}
                description={t('editors.attractionBrief.beadsHint')}
            >
                <MultiSelect
                    searchable
                    data={options}
                    value={strs(draft.beadIds)}
                    onChange={(values) => set('beadIds', ids(values))}
                />
                <Stack gap={4}>
                    {draft.beadIds.map((id, index) => (
                        <Group key={id} gap='xs' wrap='nowrap'>
                            <Text size='sm' fw={600} w={20}>
                                {index + 1}.
                            </Text>
                            <Text size='sm' style={{ flex: 1 }}>
                                {byId.get(id)?.claim || `#${id}`}
                            </Text>
                            <ActionIcon
                                variant='subtle'
                                size='sm'
                                onClick={() => moveBead(index, -1)}
                            >
                                <IconArrowUp size={14} />
                            </ActionIcon>
                            <ActionIcon
                                variant='subtle'
                                size='sm'
                                onClick={() => moveBead(index, 1)}
                            >
                                <IconArrowDown size={14} />
                            </ActionIcon>
                        </Group>
                    ))}
                </Stack>
            </Section>

            <Section
                boxed
                title={t('editors.attractionBrief.cards')}
                description={t('editors.attractionBrief.cardsHint')}
            >
                {draft.cards.map((card, index) => (
                    <Paper key={index} withBorder p='xs' radius='md'>
                        <Group gap='xs' align='flex-end' wrap='nowrap'>
                            <Text size='sm' fw={600}>
                                {index + 1}.
                            </Text>
                            <TextInput
                                size='xs'
                                w={220}
                                label={t('editors.attractionBrief.cardTitle')}
                                value={card.titleIdea || ''}
                                onChange={(e) => {
                                    const value = e.currentTarget.value;
                                    set(
                                        'cards',
                                        draft.cards.map((c, i) =>
                                            i === index ? { ...c, titleIdea: value } : c,
                                        ),
                                    );
                                }}
                            />
                            <MultiSelect
                                size='xs'
                                style={{ flex: 1 }}
                                searchable
                                label={t('editors.attractionBrief.cardFacts')}
                                data={options}
                                value={strs(card.factIds)}
                                onChange={(values) =>
                                    set(
                                        'cards',
                                        draft.cards.map((c, i) =>
                                            i === index ? { ...c, factIds: ids(values) } : c,
                                        ),
                                    )
                                }
                            />
                            <ActionIcon
                                color='red'
                                variant='subtle'
                                onClick={() =>
                                    set(
                                        'cards',
                                        draft.cards.filter((_, i) => i !== index),
                                    )
                                }
                            >
                                <IconTrash size={16} />
                            </ActionIcon>
                        </Group>
                    </Paper>
                ))}
                {draft.cards.length < 3 && (
                    <Group>
                        <Button
                            size='xs'
                            variant='light'
                            leftSection={<IconPlus size={14} />}
                            onClick={() =>
                                set('cards', [...draft.cards, { titleIdea: '', factIds: [] }])
                            }
                        >
                            {t('editors.attractionBrief.addCard')}
                        </Button>
                    </Group>
                )}
            </Section>

            <Section boxed title={t('editors.attractionBrief.extras')}>
                <Select
                    clearable
                    searchable
                    label={t('editors.attractionBrief.funFact')}
                    data={options}
                    value={draft.funFactId ? String(draft.funFactId) : null}
                    onChange={(value) => set('funFactId', value ? Number(value) : null)}
                />
                <MultiSelect
                    searchable
                    label={t('editors.attractionBrief.lookFor')}
                    data={options}
                    value={strs(draft.lookForIds)}
                    onChange={(values) => set('lookForIds', ids(values))}
                />
                <Textarea
                    label={t('editors.attractionBrief.notes')}
                    autosize
                    minRows={2}
                    value={draft.notes}
                    onChange={(e) => set('notes', e.currentTarget.value)}
                />
            </Section>

            <Group justify='flex-end'>
                <Button
                    variant='default'
                    loading={saving}
                    onClick={() => save(AttractionStoryBriefStatusEnum.DRAFT)}
                >
                    {t('editors.attractionBrief.saveDraft')}
                </Button>
                <Button
                    color='green'
                    loading={saving}
                    disabled={
                        !draft.promise.trim() || !draft.spine.trim() || draft.beadIds.length === 0
                    }
                    onClick={() => save(AttractionStoryBriefStatusEnum.APPROVED)}
                >
                    {t('editors.attractionBrief.approve')}
                </Button>
            </Group>
        </Stack>
    );
};
